import type {
  ContentfulImageOptionExecSize,
  ContentfulImageOptions,
} from '@/types';
import type { AssetDetails, AssetFields } from 'contentful';

export interface ContentfulImage {
  parsable: boolean;
  /** Fallback URL for the plain <img> element. */
  src: string;
  /** Multi-width srcset for the WebP <source>. */
  webpSrcSet: string;
  /** Multi-width srcset in the original format. */
  srcSet: string;
  /** Intrinsic dimensions for the width/height attributes (CLS). */
  width: number;
  height: number;
}

const DEFAULT_SIZES: ContentfulImageOptionExecSize[] = [
  { width: 350 },
  { width: 600 },
  { width: 900 },
  { width: 1200 },
  { width: 1920 },
];

/**
 * Builds responsive Contentful image URLs. Pure and deterministic so the
 * full <picture> markup (srcset + sizes) is rendered at build time and the
 * browser — not client-side JS — picks the right candidate. This keeps the
 * LCP image discoverable in the initial HTML.
 */
export function getContentfulImage(
  image: AssetFields,
  options?: ContentfulImageOptions
): ContentfulImage {
  const file = image.file;
  if (!file) {
    return {
      parsable: false,
      src: '#',
      webpSrcSet: '',
      srcSet: '',
      width: -1,
      height: -1,
    };
  }

  // Contentful returns protocol-relative URLs (//images.ctfassets.net/...).
  const rawUrl = file.url as string;
  const url = rawUrl.startsWith('//') ? `https:${rawUrl}` : rawUrl;
  const details = file.details as AssetDetails;
  const intrinsicWidth = details.image?.width || -1;
  const intrinsicHeight = details.image?.height || -1;
  const parsable =
    file.contentType === 'image/png' ||
    file.contentType === 'image/jpeg' ||
    file.contentType === 'image/jpg';

  if (!parsable) {
    return {
      parsable,
      src: url,
      webpSrcSet: '',
      srcSet: '',
      width: intrinsicWidth,
      height: intrinsicHeight,
    };
  }

  const aspectRatio =
    intrinsicWidth > 0 && intrinsicHeight > 0
      ? intrinsicWidth / intrinsicHeight
      : 1;

  let candidates =
    options?.sizes?.exec && options.sizes.exec.length > 0
      ? options.sizes.exec
      : DEFAULT_SIZES;
  // Never request an upscale of the source image.
  if (intrinsicWidth > 0) {
    const withinSource = candidates.filter((c) => c.width <= intrinsicWidth);
    candidates = withinSource.length > 0 ? withinSource : [candidates[0]];
  }

  const query = (size: ContentfulImageOptionExecSize, webp: boolean) =>
    [
      ...(webp ? ['fm=webp'] : []),
      `q=${options?.quality || 80}`,
      `fit=${options?.position ? options.position : 'thumb'}`,
      `w=${size.width}${size.height ? `&h=${size.height}` : ''}`,
    ].join('&');

  const webpSrcSet = candidates
    .map((size) => `${url}?${query(size, true)} ${size.width}w`)
    .join(', ');
  const srcSet = candidates
    .map((size) => `${url}?${query(size, false)} ${size.width}w`)
    .join(', ');

  // Mid-sized fallback for browsers that ignore srcset entirely.
  const fallback = candidates.reduce((best, size) =>
    Math.abs(size.width - 900) < Math.abs(best.width - 900) ? size : best
  );

  return {
    parsable,
    src: `${url}?${query(fallback, false)}`,
    webpSrcSet,
    srcSet,
    width: fallback.width,
    height: fallback.height
      ? fallback.height
      : Math.round(fallback.width / aspectRatio),
  };
}
